import React, { useEffect, useState } from 'react';
import portalApi from '../../../../lib/portalApi';
import { api } from '../../../../lib/acceleratorApi';
import {
  Users,
  CheckSquare,
  FileText,
  Building2,
  Calendar,
  Plus,
  Trash2,
  Share2,
  Eye,
  CheckCircle2,
  Clock,
  X,
  AlertCircle,
  TrendingUp,
} from 'lucide-react';

export default function AdminMentorControls({ onClose }) {
  const [activeTab, setActiveTab] = useState('tasks'); // 'tasks' | 'activity' | 'assign_startup'
  const [mentors, setMentors] = useState([]);
  const [startups, setStartups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Task Assignment State
  const [selectedMentorId, setSelectedMentorId] = useState('');
  const [taskKind, setTaskKind] = useState('Task'); // 'Task' | 'KRA' | 'KPI'
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDesc, setTaskDesc] = useState('');
  const [taskPeriod, setTaskPeriod] = useState('monthly');
  const [taskTarget, setTaskTarget] = useState('');
  const [taskUnit, setTaskUnit] = useState('sessions');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [submittingTask, setSubmittingTask] = useState(false);

  // Activity Inspection State
  const [activeMentorActivity, setActiveMentorActivity] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(false);

  // Startup Mentor Assignment State
  const [selectedStartupId, setSelectedStartupId] = useState('');
  const [assignMentorId, setAssignMentorId] = useState('');
  const [assigningStartup, setAssigningStartup] = useState(false);

  // Document Sharing State
  const [docStartupId, setDocStartupId] = useState('');
  const [startupDocs, setStartupDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  const loadStartupDocs = async (sid) => {
    if (!sid) return;
    try {
      setLoadingDocs(true);
      const res = await api.getDocuments(sid);
      setStartupDocs(res?.documents || res || []);
    } catch (err) {
      console.error('Failed to load startup documents:', err);
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleToggleDocShare = async (docId, mentorId, currentlyShared) => {
    try {
      if (currentlyShared) {
        await api.revokeDocumentFromMentor(docId, mentorId);
      } else {
        await api.shareDocumentWithMentor(docId, mentorId);
      }
      loadStartupDocs(docStartupId);
    } catch (err) {
      alert(err.message || 'Failed to update document sharing');
    }
  };

  const loadBaseData = async () => {
    try {
      setLoading(true);
      setError('');
      const [mRes, sRes] = await Promise.all([
        portalApi.getAdminMentors(),
        api.getStartups(),
      ]);
      setMentors(mRes?.mentors || []);
      setStartups(sRes || []);
      if (mRes?.mentors?.[0]) setSelectedMentorId(mRes.mentors[0].id);
      if (sRes?.[0]) {
        setSelectedStartupId(sRes[0]._id);
        setDocStartupId(sRes[0]._id);
        loadStartupDocs(sRes[0]._id);
      }
    } catch (err) {
      console.error('Failed to load admin mentor data:', err);
      setError(err.message || 'Failed to load mentor administration data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBaseData();
  }, []);

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!selectedMentorId || !taskTitle.trim()) {
      setError('Please select a mentor and enter a title');
      return;
    }
    try {
      setSubmittingTask(true);
      setError('');
      setSuccess('');
      await portalApi.adminAssignMentorTask({
        mentorId: selectedMentorId,
        kind: taskKind,
        title: taskTitle.trim(),
        description: taskDesc.trim(),
        period: taskPeriod,
        target: taskTarget ? Number(taskTarget) : null,
        unit: taskUnit.trim(),
        dueDate: taskDueDate || null,
      });
      setTaskTitle('');
      setTaskDesc('');
      setTaskTarget('');
      setSuccess('Task/KRA/KPI successfully assigned to mentor.');
      setTimeout(() => setSuccess(''), 4000);
      loadBaseData();
    } catch (err) {
      setError(err.message || 'Failed to assign task');
    } finally {
      setSubmittingTask(false);
    }
  };

  const handleInspectMentor = async (mentorId) => {
    try {
      setLoadingActivity(true);
      const res = await portalApi.getAdminMentorActivity(mentorId);
      setActiveMentorActivity(res);
    } catch (err) {
      alert(err.message || 'Failed to load mentor activity');
    } finally {
      setLoadingActivity(false);
    }
  };

  const handleAssignMentorToStartup = async (e) => {
    e.preventDefault();
    if (!selectedStartupId || !assignMentorId) return;
    try {
      setAssigningStartup(true);
      setError('');
      await api.assignMentor(selectedStartupId, assignMentorId);
      setSuccess('Mentor successfully assigned to startup.');
      setTimeout(() => setSuccess(''), 4000);
      loadBaseData();
    } catch (err) {
      setError(err.message || 'Failed to assign mentor to startup');
    } finally {
      setAssigningStartup(false);
    }
  };

  const handleRemoveMentorFromStartup = async (startupId, mentorId) => {
    if (!window.confirm('Remove this mentor from the startup?')) return;
    try {
      await api.removeMentor(startupId, mentorId);
      setSuccess('Mentor unassigned from startup.');
      setTimeout(() => setSuccess(''), 4000);
      loadBaseData();
    } catch (err) {
      alert(err.message || 'Failed to remove mentor');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
      <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl">
        {/* Header matching PDF Page 8 */}
        <div className="p-5 border-b border-[#2C2C34] flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-[#F2F2F4]">Superadmin Mentor Administration</h2>
            <p className="text-xs text-[#8E8E99]">
              Assign Task / KRA / KPI, manage startup assignments, and view mentor activity.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-[#8E8E99] hover:text-[#F2F2F4] hover:bg-white/[0.05]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Selection */}
        <div className="flex items-center gap-2 px-5 pt-3 border-b border-[#2C2C34]">
          {[
            { id: 'tasks', label: 'Assign Task / KRA / KPI', icon: CheckSquare },
            { id: 'activity', label: 'Mentor Activity View', icon: Eye },
            { id: 'assign_startup', label: 'Startup Mentor Assignments', icon: Building2 },
            { id: 'share_docs', label: 'Document Sharing', icon: FileText },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setError('');
                setSuccess('');
              }}
              className={`flex items-center gap-1.5 px-4 py-2.5 rounded-t-xl text-xs font-semibold transition-all ${
                activeTab === tab.id
                  ? 'bg-[#08080A] text-[#D42B92] border-t border-x border-[#2C2C34]'
                  : 'text-[#8E8E99] hover:text-[#F2F2F4]'
              }`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto flex-1 space-y-5 text-xs">
          {error && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span>{success}</span>
            </div>
          )}

          {/* ─── TAB 1: ASSIGN TASK / KRA / KPI ─────────────────────────── */}
          {activeTab === 'tasks' && (
            <form onSubmit={handleCreateTask} className="space-y-4 max-w-2xl">
              <div>
                <label className="block text-[#8E8E99] font-medium mb-1">Select Mentor</label>
                <select
                  value={selectedMentorId}
                  onChange={(e) => setSelectedMentorId(e.target.value)}
                  required
                  className="w-full px-3.5 py-2.5 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                >
                  {mentors.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name} ({m.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[#8E8E99] font-medium mb-1">Kind</label>
                  <select
                    value={taskKind}
                    onChange={(e) => setTaskKind(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                  >
                    <option value="Task">Task (Standard assignment)</option>
                    <option value="KRA">KRA (Key Result Area)</option>
                    <option value="KPI">KPI (Key Performance Indicator)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[#8E8E99] font-medium mb-1">Reporting Period</label>
                  <select
                    value={taskPeriod}
                    onChange={(e) => setTaskPeriod(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                  >
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[#8E8E99] font-medium mb-1">Title</label>
                <input
                  type="text"
                  value={taskTitle}
                  onChange={(e) => setTaskTitle(e.target.value)}
                  placeholder="e.g. Onboard 5 new startups or Hold 8 sessions a month"
                  required
                  className="w-full px-3.5 py-2.5 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-[#8E8E99] font-medium mb-1">Numeric Target (Optional)</label>
                  <input
                    type="number"
                    value={taskTarget}
                    onChange={(e) => setTaskTarget(e.target.value)}
                    placeholder="e.g. 8"
                    className="w-full px-3 py-2.5 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                  />
                </div>
                <div>
                  <label className="block text-[#8E8E99] font-medium mb-1">Unit</label>
                  <input
                    type="text"
                    value={taskUnit}
                    onChange={(e) => setTaskUnit(e.target.value)}
                    placeholder="e.g. sessions, startups"
                    className="w-full px-3 py-2.5 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                  />
                </div>
                <div>
                  <label className="block text-[#8E8E99] font-medium mb-1">Due Date</label>
                  <input
                    type="date"
                    value={taskDueDate}
                    onChange={(e) => setTaskDueDate(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[#8E8E99] font-medium mb-1">Description / Notes</label>
                <textarea
                  value={taskDesc}
                  onChange={(e) => setTaskDesc(e.target.value)}
                  rows={2}
                  placeholder="Specific goals, criteria or expectations..."
                  className="w-full px-3.5 py-2 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                />
              </div>

              <button
                type="submit"
                disabled={submittingTask}
                className="px-5 py-2.5 rounded-xl bg-[#D42B92] hover:bg-[#b02279] text-white font-semibold transition-colors disabled:opacity-50"
              >
                {submittingTask ? 'Assigning…' : 'Assign to Mentor'}
              </button>
            </form>
          )}

          {/* ─── TAB 2: MENTOR ACTIVITY VIEW (PDF Page 8 Read-only) ─────── */}
          {activeTab === 'activity' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {mentors.map((m) => (
                  <div
                    key={m.id}
                    onClick={() => handleInspectMentor(m.id)}
                    className="p-3.5 rounded-xl bg-[#08080A] border border-[#2C2C34] hover:border-[#D42B92]/50 cursor-pointer transition-colors space-y-1.5"
                  >
                    <div className="font-bold text-[#F2F2F4]">{m.name}</div>
                    <div className="text-[11px] text-[#8E8E99] truncate">{m.email}</div>
                    <div className="flex items-center justify-between text-[11px] pt-1 border-t border-[#2C2C34]/50">
                      <span>Startups: {m.assignedStartupsCount}</span>
                      <span className="text-emerald-400 font-semibold">{m.completedTasks} Done</span>
                    </div>
                  </div>
                ))}
              </div>

              {loadingActivity ? (
                <div className="p-8 text-center text-[#8E8E99]">Loading mentor activity…</div>
              ) : activeMentorActivity ? (
                <div className="p-5 rounded-2xl bg-[#08080A] border border-[#2C2C34] space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-[#F2F2F4]">
                        Activity for {activeMentorActivity.mentor?.name}
                      </h3>
                      <div className="text-[11px] text-[#8E8E99]">
                        {activeMentorActivity.mentor?.email}
                      </div>
                    </div>
                  </div>

                  {/* Assignments List */}
                  <div className="space-y-2">
                    <div className="font-semibold text-[#8E8E99] uppercase text-[10px] tracking-wider">
                      Superadmin-Assigned Performance Work ({activeMentorActivity.assignments?.length || 0})
                    </div>
                    {activeMentorActivity.assignments?.length === 0 ? (
                      <div className="text-[#8E8E99]">No performance work assigned yet.</div>
                    ) : (
                      activeMentorActivity.assignments.map((a) => (
                        <div
                          key={a._id}
                          className="p-2.5 rounded-xl bg-[#131318] border border-[#2C2C34] flex items-center justify-between gap-3"
                        >
                          <div>
                            <span className="font-semibold text-[#F2F2F4]">{a.title}</span>
                            <span className="ml-2 text-[10px] px-1.5 py-0.2 rounded bg-white/5 text-[#8E8E99]">
                              {a.kind} · {a.period}
                            </span>
                          </div>
                          <span
                            className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                              a.status === 'Done'
                                ? 'bg-emerald-500/10 text-emerald-400'
                                : a.status === 'In Review'
                                ? 'bg-purple-500/10 text-purple-300'
                                : 'bg-white/5 text-[#8E8E99]'
                            }`}
                          >
                            {a.status}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-8 text-center text-[#8E8E99]">
                  Click on any mentor above to inspect their activity, completion status, and recent work.
                </div>
              )}
            </div>
          )}

          {/* ─── TAB 3: STARTUP MENTOR ASSIGNMENT ───────────────────────── */}
          {activeTab === 'assign_startup' && (
            <div className="space-y-5">
              <form onSubmit={handleAssignMentorToStartup} className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-[#8E8E99] font-medium mb-1">Select Startup</label>
                  <select
                    value={selectedStartupId}
                    onChange={(e) => setSelectedStartupId(e.target.value)}
                    required
                    className="w-full px-3.5 py-2 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                  >
                    {startups.map((s) => (
                      <option key={s._id} value={s._id}>
                        {s.name} ({s.founderName || s.founderEmail})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex-1 min-w-[200px]">
                  <label className="block text-[#8E8E99] font-medium mb-1">Assign Mentor</label>
                  <select
                    value={assignMentorId}
                    onChange={(e) => setAssignMentorId(e.target.value)}
                    required
                    className="w-full px-3.5 py-2 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                  >
                    <option value="">Choose mentor...</option>
                    {mentors.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={assigningStartup}
                  className="px-5 py-2 rounded-xl bg-[#D42B92] hover:bg-[#b02279] text-white font-semibold transition-colors disabled:opacity-50"
                >
                  {assigningStartup ? 'Assigning…' : 'Assign Mentor'}
                </button>
              </form>

              {/* Current Assignments Table */}
              <div className="space-y-2">
                <div className="font-semibold text-[#8E8E99] uppercase text-[10px] tracking-wider">
                  Active Startup Mentor Assignments
                </div>
                <div className="divide-y divide-[#2C2C34]/50 border border-[#2C2C34] rounded-xl overflow-hidden bg-[#08080A]">
                  {startups
                    .filter((s) => s.mentorIds?.length > 0)
                    .map((s) => (
                      <div key={s._id} className="p-3.5 flex items-center justify-between gap-4">
                        <div>
                          <div className="font-bold text-[#F2F2F4]">{s.name}</div>
                          <div className="text-[11px] text-[#8E8E99]">{s.founderName || s.founderEmail}</div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          {s.mentorIds.map((m) => {
                            const mentorObj = mentors.find((x) => x.id === (m._id || m));
                            const mentorName = mentorObj?.name || 'Mentor';
                            const mId = m._id || m;

                            return (
                              <div
                                key={mId}
                                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/5 border border-[#2C2C34] text-xs text-[#F2F2F4]"
                              >
                                <span>{mentorName}</span>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveMentorFromStartup(s._id, mId)}
                                  className="text-red-400 hover:text-red-300 p-0.5"
                                  title="Unassign mentor"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}

          {/* ─── TAB 4: DOCUMENT SHARING WITH MENTORS (PDF Page 8) ──────── */}
          {activeTab === 'share_docs' && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-[#8E8E99] font-medium mb-1">Select Startup</label>
                  <select
                    value={docStartupId}
                    onChange={(e) => {
                      setDocStartupId(e.target.value);
                      loadStartupDocs(e.target.value);
                    }}
                    className="w-full px-3.5 py-2 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                  >
                    {startups.map((s) => (
                      <option key={s._id} value={s._id}>
                        {s.name} ({s.founderName || s.founderEmail})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {loadingDocs ? (
                <div className="p-8 text-center text-[#8E8E99]">Loading startup documents…</div>
              ) : startupDocs.length === 0 ? (
                <div className="p-8 text-center text-[#8E8E99]">No documents found for this startup.</div>
              ) : (
                <div className="space-y-2">
                  <div className="font-semibold text-[#8E8E99] uppercase text-[10px] tracking-wider">
                    Startup Documents & Explicit Mentor Grants
                  </div>
                  <div className="divide-y divide-[#2C2C34]/50 border border-[#2C2C34] rounded-xl overflow-hidden bg-[#08080A]">
                    {startupDocs.map((doc) => {
                      const selectedStartup = startups.find((s) => String(s._id) === String(docStartupId));
                      const assignedMentorIds = (selectedStartup?.mentorIds || []).map((m) => String(m._id || m));
                      const sharedMentorIds = (doc.sharedWithMentorIds || []).map(String);

                      return (
                        <div key={doc._id} className="p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div>
                            <div className="font-semibold text-[#F2F2F4] text-xs">
                              {doc.heading || doc.originalName || 'Document'}
                            </div>
                            <div className="text-[11px] text-[#8E8E99]">
                              Status: {doc.status} · Type: {doc.mimeType || 'file'}
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-2">
                            {assignedMentorIds.length === 0 ? (
                              <span className="text-[11px] text-[#8E8E99] italic">No mentors assigned to startup</span>
                            ) : (
                              assignedMentorIds.map((mId) => {
                                const mObj = mentors.find((x) => String(x.id) === String(mId));
                                const mName = mObj?.name || 'Mentor';
                                const isShared = sharedMentorIds.includes(String(mId));

                                return (
                                  <button
                                    key={mId}
                                    type="button"
                                    onClick={() => handleToggleDocShare(doc._id, mId, isShared)}
                                    className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors flex items-center gap-1.5 ${
                                      isShared
                                        ? 'bg-[#321129] border-[#D42B92]/60 text-[#D42B92]'
                                        : 'bg-white/5 border-[#2C2C34] text-[#8E8E99] hover:text-[#F2F2F4]'
                                    }`}
                                  >
                                    <Share2 className="w-3 h-3" />
                                    <span>{mName}</span>
                                    <span>{isShared ? '✓ Shared' : '+ Grant'}</span>
                                  </button>
                                );
                              })
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
